import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Filter, Phone, SlidersHorizontal, X } from "lucide-react";
import { api } from "@/lib/api";
import { VehicleCard } from "@/components/VehicleCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { titleCase } from "@/lib/format";
import { useBrandConfig } from "@/hooks/useBrandConfig";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "price_asc", label: "Price: low → high" },
  { value: "price_desc", label: "Price: high → low" },
  { value: "year_desc", label: "Year: newest" },
  { value: "mileage_asc", label: "Mileage: low → high" },
];

const PAGE_SIZE = 24;
const ANY = "__any__";

/**
 * FiltersPanel — mirrors the shape of the backend `/vehicles/facets` payload.
 * Note: the `condition` filter (used vs certified) was intentionally removed
 * in the Feb 2026 polish batch — the marketplace surfaces both and lets the
 * badge on the card do the talking.
 */
const FiltersPanel = ({ facets, filters, setFilters, dealers, onApply, onClear }) => {
  const sel = (key) => filters[key] ?? "";

  const update = (key) => (value) => {
    const v = value === "" || value === ANY ? undefined : value;
    setFilters((s) => ({ ...s, [key]: v }));
  };

  return (
    <div className="space-y-6">
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Keyword</Label>
        <Input
          value={filters.q || ""}
          onChange={(e) => update("q")(e.target.value)}
          placeholder="Make, model, VIN…"
          className="mt-2"
          data-testid="filter-keyword-input"
        />
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Make</Label>
        <Select value={sel("make") || ANY} onValueChange={update("make")}>
          <SelectTrigger className="mt-2" data-testid="filter-make-select">
            <SelectValue placeholder="Any make" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any make</SelectItem>
            {facets?.makes?.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Model</Label>
        <Select value={sel("model") || ANY} onValueChange={update("model")}>
          <SelectTrigger className="mt-2" data-testid="filter-model-select">
            <SelectValue placeholder={filters.make ? "Any model" : "Any model"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any model</SelectItem>
            {facets?.models?.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Body style</Label>
        <Select value={sel("body_style") || ANY} onValueChange={update("body_style")}>
          <SelectTrigger className="mt-2" data-testid="filter-body-style-select">
            <SelectValue placeholder="Any body style" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any body style</SelectItem>
            {facets?.body_styles?.map((b) => (
              <SelectItem key={b} value={b}>
                {titleCase(b)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Min year</Label>
          <Input
            type="number"
            inputMode="numeric"
            value={filters.year_min || ""}
            onChange={(e) => update("year_min")(e.target.value)}
            placeholder={facets?.year_min || "2015"}
            className="mt-2"
            data-testid="filter-year-min-input"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Max year</Label>
          <Input
            type="number"
            inputMode="numeric"
            value={filters.year_max || ""}
            onChange={(e) => update("year_max")(e.target.value)}
            placeholder={facets?.year_max || "2024"}
            className="mt-2"
            data-testid="filter-year-max-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Min price</Label>
          <Input
            type="number"
            inputMode="numeric"
            value={filters.price_min || ""}
            onChange={(e) => update("price_min")(e.target.value)}
            placeholder="0"
            className="mt-2"
            data-testid="filter-price-min-input"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Max price</Label>
          <Input
            type="number"
            inputMode="numeric"
            value={filters.price_max || ""}
            onChange={(e) => update("price_max")(e.target.value)}
            placeholder={facets?.price_max || "85000"}
            className="mt-2"
            data-testid="filter-price-max-input"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Max mileage</Label>
        <Input
          type="number"
          inputMode="numeric"
          value={filters.mileage_max || ""}
          onChange={(e) => update("mileage_max")(e.target.value)}
          placeholder="e.g. 60000"
          className="mt-2"
          data-testid="filter-mileage-max-input"
        />
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Fuel type</Label>
        <Select value={sel("fuel_type") || ANY} onValueChange={update("fuel_type")}>
          <SelectTrigger className="mt-2" data-testid="filter-fuel-select">
            <SelectValue placeholder="Any fuel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any fuel</SelectItem>
            {facets?.fuel_types?.map((f) => (
              <SelectItem key={f} value={f}>
                {titleCase(f)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Transmission</Label>
        <Select value={sel("transmission") || ANY} onValueChange={update("transmission")}>
          <SelectTrigger className="mt-2" data-testid="filter-transmission-select">
            <SelectValue placeholder="Any" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any</SelectItem>
            {facets?.transmissions?.map((t) => (
              <SelectItem key={t} value={t}>
                {titleCase(t)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Drivetrain</Label>
        <Select value={sel("drivetrain") || ANY} onValueChange={update("drivetrain")}>
          <SelectTrigger className="mt-2" data-testid="filter-drivetrain-select">
            <SelectValue placeholder="Any" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any</SelectItem>
            {facets?.drivetrains?.map((d) => (
              <SelectItem key={d} value={d}>
                {d.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Dealer</Label>
        <Select value={sel("dealer_id") || ANY} onValueChange={update("dealer_id")}>
          <SelectTrigger className="mt-2" data-testid="filter-dealer-select">
            <SelectValue placeholder="Any dealer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any dealer</SelectItem>
            {dealers?.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-2 pt-2">
        <Button className="flex-1 bg-brand hover:bg-brand-hover text-white" onClick={onApply} data-testid="filter-apply-btn">
          Apply
        </Button>
        <Button variant="outline" onClick={onClear} data-testid="filter-clear-btn">
          Clear
        </Button>
      </div>
    </div>
  );
};

const Inventory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { bdc_phone_display, bdc_phone_href } = useBrandConfig();

  const initial = useMemo(() => {
    const obj = {};
    for (const [k, v] of searchParams.entries()) obj[k] = v;
    return obj;
  }, [searchParams]);

  const [filters, setFilters] = useState({ ...initial });
  const [sort, setSort] = useState(initial.sort || "newest");
  const [page, setPage] = useState(Number(initial.page) || 1);

  const [facets, setFacets] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Facets fetched with the current `make` filter so the Model dropdown
    // narrows to that make. Falls back to all models when no make selected.
    const params = filters.make ? { make: filters.make } : {};
    api.get("/vehicles/facets", { params })
      .then((r) => setFacets(r.data))
      .catch(() => setFacets(null));
    api.get("/dealers").then((r) => setDealers(r.data)).catch(() => setDealers([]));
  }, [filters.make]);

  const activeParams = useMemo(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== "" && k !== "page" && k !== "sort") params[k] = v;
    });
    params.sort = sort;
    params.page = page;
    params.page_size = PAGE_SIZE;
    return params;
  }, [filters, sort, page]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/vehicles", { params: activeParams })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0 }))
      .finally(() => setLoading(false));

    // Reflect state in the URL (skip page_size + defaults)
    const next = new URLSearchParams();
    Object.entries(activeParams).forEach(([k, v]) => {
      if (v == null || v === "" || k === "page_size") return;
      if (k === "sort" && v === "newest") return;
      if (k === "page" && v === 1) return;
      next.set(k, String(v));
    });
    setSearchParams(next, { replace: true });
  }, [activeParams, setSearchParams]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const dealerName = (id) => dealers.find((d) => d.id === id)?.name;

  const onApply = () => setPage(1);
  const onClear = () => {
    setFilters({});
    setSort("newest");
    setPage(1);
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Marketplace</p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
              Inventory
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              {loading ? "Loading…" : `Showing ${data.total.toLocaleString()} vehicles`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="lg:hidden"
                  data-testid="mobile-filter-trigger"
                >
                  <Filter size={16} /> Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[320px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-4">
                  <FiltersPanel
                    facets={facets}
                    filters={filters}
                    setFilters={setFilters}
                    dealers={dealers}
                    onApply={onApply}
                    onClear={onClear}
                  />
                </div>
              </SheetContent>
            </Sheet>

            <Select value={sort} onValueChange={(v) => { setSort(v); setPage(1); }}>
              <SelectTrigger className="w-[220px]" data-testid="sort-select">
                <SlidersHorizontal size={14} />
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-8">
          {/* Desktop filters */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24 bg-white border border-slate-200 rounded-2xl p-6" data-testid="desktop-filters">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-semibold">Filters</h3>
                <button
                  onClick={onClear}
                  className="text-xs text-slate-500 hover:text-brand inline-flex items-center gap-1"
                  data-testid="filter-reset-btn"
                >
                  <X size={12} /> Reset
                </button>
              </div>
              <FiltersPanel
                facets={facets}
                filters={filters}
                setFilters={setFilters}
                dealers={dealers}
                onApply={onApply}
                onClear={onClear}
              />
            </div>
          </aside>

          {/* Grid — takes the full remaining width now that the tools rail is gone. */}
          <div className="lg:col-span-9" data-testid="inventory-grid">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-[380px] bg-slate-100 rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : data.items.length === 0 ? (
              <div className="text-center py-24 bg-slate-50 rounded-2xl" data-testid="inventory-empty">
                <p className="font-display text-xl text-slate-700">No vehicles match your filters</p>
                <p className="mt-2 text-sm text-slate-500">Try loosening a couple of them.</p>
                <Button className="mt-4 bg-brand hover:bg-brand-hover text-white" onClick={onClear}>
                  Reset filters
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {data.items.map((v) => (
                    <VehicleCard
                      key={v.id}
                      vehicle={v}
                      dealerName={dealerName(v.dealer_id)}
                      isComingSoon={!!v.is_coming_soon}
                    />
                  ))}
                </div>

                {/* Pagination */}
                <div className="mt-10 flex items-center justify-between">
                  <p className="text-xs text-slate-500">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      data-testid="pagination-prev-btn"
                    >
                      <ChevronLeft size={16} /> Prev
                    </Button>
                    <Button
                      variant="outline"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      data-testid="pagination-next-btn"
                    >
                      Next <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Inventory;
